import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { CognitoService } from './cognito.service';
import { AuthStep, JwtPayload, UserSession } from '../models/auth.model';

const TOKEN_KEYS = {
  access: 'auth_token',
  id: 'id_token',
  refresh: 'refresh_token',
} as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private cognito = inject(CognitoService);
  private router = inject(Router);

  private sessionSubject = new BehaviorSubject<UserSession | null>(this.loadSession());
  session$ = this.sessionSubject.asObservable();

  private _authStep: AuthStep = 'credentials';
  private _mfaSecretCode: string | null = null;

  private challengeSession: string | null = null;
  private challengeEmail: string | null = null;
  private mfaSession: string | null = null;
  private mfaEmail: string | null = null;
  private setupSession: string | null = null;

  get authStep(): AuthStep {
    return this._authStep;
  }

  get mfaSecretCode(): string | null {
    return this._mfaSecretCode;
  }

  get currentSession(): UserSession | null {
    return this.sessionSubject.value;
  }

  get isAuthenticated(): boolean {
    const session = this.currentSession;
    if (!session) return false;
    const payload = this.decodeJwt(session.idToken);
    return !!payload && payload.exp * 1000 > Date.now();
  }

  get isAdmin(): boolean {
    return this.currentSession?.isAdmin ?? false;
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEYS.access);
  }

  getIdToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEYS.id);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEYS.refresh);
  }

  login(email: string, password: string): Observable<void> {
    return this.cognito.initiateAuth(email, password).pipe(
      switchMap(result => {
        if (result.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          this.challengeSession = result.Session ?? null;
          this.challengeEmail = email;
          this.router.navigate(['/change-password']);
          return of(undefined as void);
        }

        if (result.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
          this.mfaSession = result.Session ?? null;
          this.mfaEmail = email;
          this._authStep = 'mfa';
          return of(undefined as void);
        }

        if (result.ChallengeName === 'MFA_SETUP') {
          this.mfaEmail = email;
          return this.cognito.associateSoftwareToken(result.Session ?? '').pipe(
            tap(assocResult => {
              this._mfaSecretCode = assocResult.SecretCode;
              this.setupSession = assocResult.Session ?? result.Session ?? null;
              this._authStep = 'mfa-setup';
            }),
            map(() => undefined as void),
          );
        }

        if (result.AuthenticationResult) {
          this.storeTokens(result.AuthenticationResult);
        }

        return of(undefined as void);
      }),
    );
  }

  respondToMfa(code: string): Observable<void> {
    if (!this.mfaSession || !this.mfaEmail) {
      return throwError(() => new Error('No active MFA session'));
    }

    return this.cognito
      .respondToSoftwareTokenMfa(this.mfaSession, this.mfaEmail, code)
      .pipe(
        tap(result => {
          if (result.AuthenticationResult) {
            this.mfaSession = null;
            this.mfaEmail = null;
            this._authStep = 'credentials';
            this.storeTokens(result.AuthenticationResult);
          }
        }),
        map(() => undefined as void),
      );
  }

  verifyMfaSetup(code: string): Observable<void> {
    if (!this.setupSession || !this.mfaEmail) {
      return throwError(() => new Error('No active MFA setup session'));
    }

    const email = this.mfaEmail;

    return this.cognito.verifySoftwareToken(this.setupSession, code).pipe(
      switchMap(verifyResult =>
        this.cognito.respondToMfaSetupChallenge(verifyResult.Session, email),
      ),
      tap(challengeResult => {
        if (challengeResult.AuthenticationResult) {
          this.setupSession = null;
          this.mfaEmail = null;
          this._mfaSecretCode = null;
          this._authStep = 'credentials';
          this.storeTokens(challengeResult.AuthenticationResult);
        }
      }),
      map(() => undefined as void),
    );
  }

  completeNewPassword(newPassword: string): Observable<void> {
    if (!this.challengeSession || !this.challengeEmail) {
      return throwError(() => new Error('No active challenge session'));
    }

    return this.cognito
      .respondToNewPasswordChallenge(this.challengeSession, this.challengeEmail, newPassword)
      .pipe(
        switchMap(result => {
          if (result.ChallengeName === 'MFA_SETUP') {
            this.mfaEmail = this.challengeEmail;
            return this.cognito.associateSoftwareToken(result.Session ?? '').pipe(
              tap(assocResult => {
                this._mfaSecretCode = assocResult.SecretCode;
                this.setupSession = assocResult.Session ?? result.Session ?? null;
                this.challengeSession = null;
                this.challengeEmail = null;
                this._authStep = 'mfa-setup';
                this.router.navigate(['/login']);
              }),
              map(() => undefined as void),
            );
          }

          if (result.AuthenticationResult) {
            this.challengeSession = null;
            this.challengeEmail = null;
            this.storeTokens(result.AuthenticationResult);
          }

          return of(undefined as void);
        }),
      );
  }

  refreshSession(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    return this.cognito.refreshToken(refreshToken).pipe(
      tap(result => {
        if (result.AuthenticationResult) {
          this.storeTokens({
            ...result.AuthenticationResult,
            RefreshToken: refreshToken,
          });
        }
      }),
      map(() => undefined as void),
    );
  }

  logout(): void {
    sessionStorage.removeItem(TOKEN_KEYS.access);
    sessionStorage.removeItem(TOKEN_KEYS.id);
    sessionStorage.removeItem(TOKEN_KEYS.refresh);
    this.sessionSubject.next(null);
    this.router.navigate(['/login']);
  }

  hasChallengeSession(): boolean {
    return !!this.challengeSession;
  }

  resetAuthStep(): void {
    this._authStep = 'credentials';
    this.mfaSession = null;
    this.mfaEmail = null;
    this.setupSession = null;
    this._mfaSecretCode = null;
  }

  private storeTokens(tokens: {
    AccessToken: string;
    IdToken: string;
    RefreshToken: string;
    ExpiresIn?: number;
    TokenType?: string;
  }): void {
    sessionStorage.setItem(TOKEN_KEYS.access, tokens.AccessToken);
    sessionStorage.setItem(TOKEN_KEYS.id, tokens.IdToken);
    sessionStorage.setItem(TOKEN_KEYS.refresh, tokens.RefreshToken);

    const payload = this.decodeJwt(tokens.IdToken);
    const session: UserSession = {
      email: payload?.email ?? '',
      groups: payload?.['cognito:groups'] ?? [],
      accessToken: tokens.AccessToken,
      idToken: tokens.IdToken,
      refreshToken: tokens.RefreshToken,
      isAdmin: (payload?.['cognito:groups'] ?? []).includes('sc-admin'),
    };

    this.sessionSubject.next(session);
  }

  private loadSession(): UserSession | null {
    const idToken = sessionStorage.getItem(TOKEN_KEYS.id);
    const accessToken = sessionStorage.getItem(TOKEN_KEYS.access);
    const refreshToken = sessionStorage.getItem(TOKEN_KEYS.refresh);

    if (!idToken || !accessToken || !refreshToken) return null;

    const payload = this.decodeJwt(idToken);
    if (!payload || payload.exp * 1000 <= Date.now()) {
      sessionStorage.removeItem(TOKEN_KEYS.access);
      sessionStorage.removeItem(TOKEN_KEYS.id);
      sessionStorage.removeItem(TOKEN_KEYS.refresh);
      return null;
    }

    return {
      email: payload.email ?? '',
      groups: payload['cognito:groups'] ?? [],
      accessToken,
      idToken,
      refreshToken,
      isAdmin: (payload['cognito:groups'] ?? []).includes('sc-admin'),
    };
  }

  private decodeJwt(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(payload));
    } catch {
      return null;
    }
  }
}
